import React, { useEffect, useState } from 'react';
import { Arrow } from '../icons/arrow';

import styles from './grid.module.css';

/**
 * @param {Object} props
 * @param {Object.<string, {label: string, sortable?: boolean, size?: number|string, passOnClick?: boolean}>} [props.columns={}] Columns to render
 * @param {(Object.<string, React.ReactNode|string>)[]|({id: string, title: string, items: Object.<string, React.ReactNode|string>})[]} [props.data=[]] Grid Data
 * @param {boolean} [props.grouped=false] Is the data grouped
 * @param {boolean} [props.stickyHeader=true] Is the header sticky? Requires a parent to be `position:relative`
 * @param {{field: string|null, direction: 'up'|'down'}|null} [props.initialSort] Initial sort to use
 * @param {function({field: string|null, direction: 'up'|'down'}): void} [props.onSort] Called when a column is sorted
 * @param {string} [props.className=''] ClassName
 * @param {Object} [props.style={}] Styles
 */
export const Grid = ({
    columns = {},
    data = [],
    grouped = false,
    stickyHeader = true,
    initialSort = null,
    onSort = () => {},
    className = '',
    style = {},
    // @ts-ignore
    ...props
}) => {
    /** @type [{field: string|null, direction: 'up'|'down'}, function({field: string|null, direction: 'up'|'down'}): void] */
    // @ts-ignore
    const [sort, setSort] = useState(
        initialSort ?? { field: null, direction: 'down' }
    );

    //Update filter when table sort order changes
    useEffect(() => {
        onSort(sort);
    }, [sort]);

    const sortBy = (field) => {
        setSort({
            field:
                sort.field !== field || sort.direction === 'up' ? field : null,
            direction:
                sort.field !== field || sort.direction === 'up'
                    ? sort.field === field && sort.direction === 'up'
                        ? 'down'
                        : 'up'
                    : sort.direction,
        });
    };

    return (
        <table
            className={styles.Grid + ' ' + className}
            style={{
                // @ts-ignore
                '--columns': Object.values(columns)
                    .map(({ size }) =>
                        typeof size === 'string'
                            ? size
                            : 'minmax(0, ' + (size ?? 1) + 'fr)'
                    )
                    .join(' '),
                ...style,
            }}
            {...props}>
            <thead className={stickyHeader ? styles.Sticky : ''}>
                <tr>
                    {Object.entries(columns).map(([key, c]) => (
                        <th
                            key={key}
                            className={
                                sort.field === key ? styles.ShowArrow : ''
                            }
                            onClick={() => {
                                if (c.sortable == null || c.sortable)
                                    sortBy(key);
                            }}>
                            {c.label}
                            <Arrow direction={sort.direction} />
                        </th>
                    ))}
                </tr>
            </thead>
            {data.length === 0 ? (
                <tbody>
                    <tr>
                        <td className={styles.NoResults}>
                            <div>No results</div>
                        </td>
                    </tr>
                </tbody>
            ) : grouped ? (
                <>
                    {data.map((g) => (
                        <tbody key={g.id?.toString()}>
                            <tr className='category'>
                                <td>{g.title}</td>
                            </tr>

                            {g.items.map((r, i) => (
                                <tr
                                    key={r.id?.toString() ?? i}
                                    className={
                                        (r.selected
                                            ? styles.Selected + ' '
                                            : '') + (r.className ?? '')
                                    }>
                                    {Object.entries(columns).map(
                                        ([key, value]) => (
                                            <td
                                                key={key}
                                                onClick={
                                                    value.passOnClick
                                                        ? // @ts-ignore
                                                          (e) => {
                                                              if (
                                                                  e.target
                                                                      .tagName ==
                                                                  'TD'
                                                              )
                                                                  e.target.children[0].click();
                                                          }
                                                        : undefined
                                                }>
                                                {r[key]}
                                            </td>
                                        )
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    ))}
                </>
            ) : (
                <tbody>
                    {data.map((r, i) => (
                        <tr
                            key={r.id?.toString() ?? i}
                            className={
                                (r.selected ? styles.Selected + ' ' : '') +
                                (r.className ?? '')
                            }>
                            {Object.entries(columns).map(([key, value]) => (
                                <td
                                    key={key}
                                    onClick={
                                        value.passOnClick
                                            ? // @ts-ignore
                                              (e) => {
                                                  if (e.target.tagName == 'TD')
                                                      e.target.children[0].click();
                                              }
                                            : undefined
                                    }>
                                    {r[key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            )}
        </table>
    );
};
